import { motion } from 'motion/react';
import { useEffect } from 'react';
import type { ReactNode } from 'react';

const EASE = [0.165, 0.84, 0.44, 1] as const;

/** Nested modals are avoided in this app, but a ref-count keeps unlock correct. */
let scrollLockCount = 0;

function lockPageScroll() {
  scrollLockCount += 1;
  if (scrollLockCount === 1) {
    document.documentElement.classList.add('modal-open');
  }
}

function unlockPageScroll() {
  scrollLockCount = Math.max(0, scrollLockCount - 1);
  if (scrollLockCount === 0) {
    document.documentElement.classList.remove('modal-open');
  }
}

/**
 * A centred dialog with a backdrop.
 *
 * The caller is responsible for wrapping its conditional rendering in
 * `<AnimatePresence>` — `AnimatePresence` has to be the stable element that
 * *stays mounted* across the open/close toggle so it can detect this
 * component leaving the tree and play the `exit` transition before actually
 * removing it. Putting `AnimatePresence` in here instead would unmount along
 * with everything else and the exit would never play.
 *
 * `dismissible = false` is for the one case in this app where closing must
 * be an explicit, informed choice: the API key secret reveal. No backdrop
 * click, no Escape, until the caller says otherwise.
 */
export function Modal({
  title,
  onClose,
  children,
  dismissible = true,
  wide = false,
}: {
  title: string;
  onClose?: () => void;
  children: ReactNode;
  dismissible?: boolean;
  /** Wider dialog for content-heavy views such as code snippets. */
  wide?: boolean;
}) {
  useEffect(() => {
    lockPageScroll();
    return unlockPageScroll;
  }, []);

  useEffect(() => {
    if (!dismissible || !onClose) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose?.();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [dismissible, onClose]);

  return (
    <motion.div
      className="modal-overlay"
      onClick={dismissible ? onClose : undefined}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.18 }}
    >
      <motion.div
        className={wide ? 'modal modal--wide' : 'modal'}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        onClick={(e) => e.stopPropagation()}
        initial={{ opacity: 0, y: 16, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 10, scale: 0.98 }}
        transition={{ duration: 0.18, ease: EASE }}
      >
        <h2 id="modal-title">{title}</h2>
        <div className="modal-scroll">{children}</div>
      </motion.div>
    </motion.div>
  );
}
